import React, { Suspense, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import type { Variants } from 'framer-motion';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { VISUAL, useSceneLayout } from './layout';
import { BACKDROP_IMAGE } from './Backdrop';
import HardwareVisual from './HardwareVisual';
import WarehouseVisual from './WarehouseVisual';

// three.js lives in its own chunk so the copy paints before the visual loads.
const EarthScene = React.lazy(() => import('./EarthScene'));

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];
const SITE3S = process.env.REACT_APP_SITE3S === '1';

const fade: Variants = {
  hidden: { opacity: 0, y: 26 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 1, delay: 0.5 + i * 0.13, ease: EASE },
  }),
};

// /site3: the copy fades out at the hero's bottom edge as it scrolls away instead of
// being sliced off by it (the page below is lighter than the hero there).
const CopyFade: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  VISUAL === 'warehouse' ? <div className="lp-wh-copyfade relative z-10">{children}</div> : <>{children}</>;

const CosmosHero: React.FC = () => {
  const ref = useRef<HTMLElement>(null);
  const [ready, setReady] = useState(false);
  const layout = useSceneLayout();

  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const sceneScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const sceneY = useTransform(scrollYProgress, [0, 1], ['0%', '16%']);
  const copyY = useTransform(scrollYProgress, [0, 1], ['0%', '38%']);
  const copyOpacity = useTransform(scrollYProgress, [0, 0.55], [1, 0]);
  const bandOpacity = useTransform(scrollYProgress, [0, 0.4], [1, 0]);

  return (
    <section ref={ref} className={`lp-hero relative z-[1] w-full overflow-hidden text-white${SITE3S ? ' lp-hero--site3s' : ''}`}>
      {VISUAL === 'warehouse' ? (
        // Keep the supplied photo steady and let its bottom edge fade into the page.
        <div className="lp-wh-fade absolute inset-0">
          <div className="absolute inset-0">
            <WarehouseVisual />
          </div>
        </div>
      ) : (
        <motion.div style={{ scale: sceneScale, y: sceneY }} className="absolute inset-0">
          <div className={`lp-placeholder lp-placeholder--${VISUAL} lp-placeholder--${layout} ${ready ? 'is-hidden' : ''}`} />
          <Suspense fallback={null}>
            {VISUAL === 'hardware' ? (
              <HardwareVisual layout={layout} onReady={() => setReady(true)} />
            ) : (
              <EarthScene layout={layout} onReady={() => setReady(true)} />
            )}
          </Suspense>
        </motion.div>
      )}

      {/* the photo's cloud bank again, in front of the visual, so it sinks into the clouds
          (not over the warehouse photo, which is the real place, not a scene) */}
      {VISUAL !== 'warehouse' && (
        <motion.div style={{ opacity: bandOpacity }} className="lp-cloudband" aria-hidden="true">
          <img src={BACKDROP_IMAGE} alt="" className="lp-backdrop-img" draggable={false} />
        </motion.div>
      )}
      {VISUAL !== 'warehouse' && <div className="lp-vignette absolute inset-0 z-[4]" />}
      {VISUAL !== 'warehouse' && <div className="lp-grain absolute inset-0 z-[5]" />}

      <CopyFade>
        <motion.div
          style={{ y: copyY, opacity: copyOpacity }}
          className="lp-hero-copy relative z-10 flex min-h-[100svh] flex-col justify-end px-5 pb-24 pt-28 sm:px-8 lg:px-12"
        >
          <div className="max-w-2xl">
            <motion.p
              custom={0}
              variants={fade}
              initial="hidden"
              animate="show"
              className="lp-hero-eyebrow flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.32em] text-white/55"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#0E9ED5] shadow-[0_0_14px_#0E9ED5]" />
              {VISUAL === 'warehouse'
                ? 'Global ICT logistics · Bonded at Changi Airport · 140+ countries'
                : 'Global ICT logistics · IOR / EOR · 140+ countries'}
            </motion.p>

            <motion.h1
              custom={1}
              variants={fade}
              initial="hidden"
              animate="show"
              className="lp-display mt-5 text-[2.5rem] text-white sm:text-[3.5rem] lg:text-[4.6rem] xl:text-[5.2rem]"
            >
              Move ICT
              <br />
              anywhere
              <br />
              on Earth
            </motion.h1>

            <motion.p
              custom={2}
              variants={fade}
              initial="hidden"
              animate="show"
              className="lp-hero-description mt-6 max-w-xl text-[15px] leading-7 text-white/70 sm:text-base sm:leading-8"
            >
              {VISUAL === 'warehouse'
                ? 'Importer of Record, freight, customs clearance and bonded warehousing in the Changi Airport Free Trade Zone. One partner and one point of accountability, from Singapore to 140+ countries.'
                : 'Importer of Record, freight, customs, warehousing and deployment across 140+ countries. One partner and one point of accountability, from Singapore to wherever your hardware needs to be.'}
            </motion.p>

            <motion.div
              custom={3}
              variants={fade}
              initial="hidden"
              animate="show"
              className="mt-9 flex flex-wrap items-center gap-3"
            >
              <Link to="/contact-us" className="lp-btn-primary">
                Get a quote
              </Link>
              <Link to="/global-coverage" className="lp-btn-secondary">
                Explore coverage
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </CopyFade>

      {VISUAL === 'warehouse' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute right-6 top-24 z-10 hidden lg:block"
        >
          <motion.p
            style={{ opacity: copyOpacity }}
            className="flex items-center gap-2 rounded-full border border-white/15 bg-black/30 px-3.5 py-1.5 text-[11px] text-white/75 backdrop-blur-md"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#0E9ED5]" />
            Our bonded warehouse, Changi Airport FTZ
          </motion.p>
        </motion.div>
      )}

      <motion.button
        type="button"
        onClick={() => {
          const next = document.getElementById('lp-stats');
          if (next) next.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-7 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-white/40 transition-colors hover:text-white/80 md:flex"
        aria-label="Scroll to content"
      >
        <span className="flex h-9 w-5 items-start justify-center rounded-full border border-white/25 p-1">
          <span className="lp-cue-dot h-1.5 w-1.5 rounded-full bg-white/80" />
        </span>
        Scroll
      </motion.button>
    </section>
  );
};

export default CosmosHero;
